import React, { useState, useEffect } from 'react';
import {
    Clock, Check, X, Calendar, MapPin, User as UserIcon,
    AlertCircle, MessageSquare, RefreshCw, DollarSign, Users,
    Link as LinkIcon, Tag, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import api from '../../../api';
import { format, differenceInDays, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';

const URGENCY_THRESHOLD_DAYS = 7;

export default function AdminPendingEvents({ onStatusChange }) {
    const { showAlert } = useAuth();
    const [pendingEvents, setPendingEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [rejectionReason, setRejectionReason] = useState({});
    const [showRejectDialog, setShowRejectDialog] = useState(null);
    const [editNote, setEditNote] = useState({});
    const [showEditDialog, setShowEditDialog] = useState(null);
    const [expanded, setExpanded] = useState({});

    useEffect(() => { loadPendingEvents(); }, []);

    const loadPendingEvents = async () => {
        try {
            setLoading(true);
            const data = await api.getPendingEvents();
            setPendingEvents(data);
        } catch (error) {
            console.error('Error loading pending events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = (eventId) => {
        showAlert(
            'Approve Event',
            'Approve this event and make it public?',
            'success',
            async () => {
                try {
                    setActionLoading(eventId);
                    await api.approveEvent(eventId);
                    await loadPendingEvents();
                    if (onStatusChange) onStatusChange();
                    showAlert('Success', 'Event approved successfully.', 'success');
                } catch (error) {
                    showAlert('Error', 'Error approving event: ' + error.message, 'error');
                } finally {
                    setActionLoading(null);
                }
            },
            true,
            'Approve & Publish'
        );
    };

    const handleReject = async (eventId) => {
        const reason = rejectionReason[eventId] || '';
        try {
            setActionLoading(eventId);
            await api.rejectEvent(eventId, reason);
            await loadPendingEvents();
            if (onStatusChange) onStatusChange();
            setShowRejectDialog(null);
            setRejectionReason({});
            showAlert('Rejected', 'Event has been rejected.', 'success');
        } catch (error) {
            showAlert('Error', 'Error rejecting event: ' + error.message, 'error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleRequestEdits = async (eventId) => {
        const note = editNote[eventId] || '';
        try {
            setActionLoading(eventId);
            await api.requestEventEdits(eventId, note);
            await loadPendingEvents();
            if (onStatusChange) onStatusChange();
            setShowEditDialog(null);
            setEditNote({});
            showAlert('Edits Requested', 'The partner has been notified of the requested edits.', 'success');
        } catch (error) {
            showAlert('Error', 'Error requesting edits: ' + error.message, 'error');
        } finally {
            setActionLoading(null);
        }
    };

    const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    return (
        <div>
            {/* Page Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="admin-page-title" style={{ marginBottom: '8px' }}>Pending Approvals</h1>
                    <p style={{ color: 'var(--v2-text-muted)' }}>
                        {pendingEvents.length} event{pendingEvents.length !== 1 ? 's' : ''} awaiting review
                    </p>
                </div>
                <button 
                    onClick={() => { loadPendingEvents(); if(onStatusChange) onStatusChange(); }} 
                    style={{ 
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 20px', borderRadius: '12px',
                        background: 'white', border: '1px solid var(--v2-border)',
                        color: 'var(--v2-text-main)', fontWeight: '600', fontSize: '14px',
                        cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                >
                    <RefreshCw size={16} /> Refresh
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="admin-card-premium" style={{ padding: '60px', textAlign: 'center' }}>
                    <div style={{ width: 40, height: 40, border: '3px solid var(--admin-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ color: 'var(--admin-text-muted)' }}>Loading pending events...</p>
                </div>
            ) : pendingEvents.length === 0 ? (
                <div className="admin-card-premium" style={{ padding: '80px', textAlign: 'center' }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #10b98120, #10b98140)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                        <Check size={36} style={{ color: '#10b981' }} />
                    </div>
                    <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 8, color: 'var(--admin-text-main)' }}>All Caught Up!</h3>
                    <p style={{ color: 'var(--admin-text-muted)' }}>No events are currently awaiting review. Check back when partners submit new events.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {pendingEvents.map((event) => {
                        const isExpanded = expanded[event.id];
                        const submittedDaysAgo = event.created_at
                            ? differenceInDays(new Date(), new Date(event.created_at))
                            : null;
                        const isUrgent = submittedDaysAgo !== null && submittedDaysAgo >= URGENCY_THRESHOLD_DAYS;
                        const isLoading = actionLoading === event.id;

                        return (
                            <div key={event.id} className="admin-card-premium" style={{ overflow: 'hidden', border: isUrgent ? '1.5px solid #ef444440' : undefined }}>

                                {/* Urgent Banner */}
                                {isUrgent && (
                                    <div style={{ background: 'linear-gradient(90deg, #ef444415, transparent)', padding: '8px 24px', borderBottom: '1px solid #ef444420', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <AlertCircle size={14} style={{ color: '#ef4444' }} />
                                        <span style={{ fontSize: '0.82rem', color: '#ef4444', fontWeight: 600 }}>
                                            Submitted {submittedDaysAgo} days ago — needs attention
                                        </span>
                                    </div>
                                )}

                                {/* Main Card Body */}
                                <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 0 }}>

                                    {/* Image Column */}
                                    <div style={{ position: 'relative', minHeight: 200 }}>
                                        <img
                                            src={event.image_url || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400'}
                                            alt={event.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400'; }}
                                        />
                                        {/* Category on image */}
                                        <div style={{ position: 'absolute', top: 12, left: 12 }}>
                                            <span style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', color: '#fff', fontSize: '0.75rem', fontWeight: 600, padding: '4px 10px', borderRadius: 20 }}>
                                                {event.category || 'Uncategorised'}
                                            </span>
                                        </div>
                                        {/* Time ago badge */}
                                        {event.created_at && (
                                            <div style={{ position: 'absolute', bottom: 12, left: 12 }}>
                                                <span style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', color: '#cbd5e1', fontSize: '0.72rem', padding: '3px 8px', borderRadius: 20 }}>
                                                    {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Details Column */}
                                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 0 }}>

                                        {/* Title Row */}
                                        <div style={{ marginBottom: 12 }}>
                                            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--admin-text-main)', marginBottom: 6 }}>
                                                {event.title}
                                            </h3>

                                            {/* Submitter pill */}
                                            {(event.submitter_name || event.submitter_email) && (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)', marginBottom: 8 }}>
                                                    <UserIcon size={12} style={{ color: '#6366f1' }} />
                                                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1' }}>
                                                        {event.submitter_name || 'Partner'}
                                                        {event.submitter_email && <span style={{ fontWeight: 400, color: 'var(--admin-text-muted)' }}> · {event.submitter_email}</span>}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Description */}
                                        <p style={{ fontSize: '0.9rem', color: 'var(--admin-text-muted)', lineHeight: 1.6, marginBottom: 16, display: '-webkit-box', WebkitLineClamp: isExpanded ? 'unset' : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {event.description}
                                        </p>

                                        {/* Meta Info Grid */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginBottom: 16 }}>
                                            {event.date && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>
                                                    <Calendar size={14} style={{ color: 'var(--admin-primary)', flexShrink: 0 }} />
                                                    <span>{format(new Date(event.date), 'EEE, MMM d yyyy · h:mm a')}</span>
                                                </div>
                                            )}
                                            {event.location && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>
                                                    <MapPin size={14} style={{ color: 'var(--admin-secondary)', flexShrink: 0 }} />
                                                    <span>{event.location}</span>
                                                </div>
                                            )}
                                            {event.organizer && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>
                                                    <UserIcon size={14} style={{ color: 'var(--admin-accent)', flexShrink: 0 }} />
                                                    <span>{event.organizer}</span>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem' }}>
                                                <DollarSign size={14} style={{ color: event.price > 0 ? '#6366f1' : '#10b981', flexShrink: 0 }} />
                                                <span style={{ fontWeight: 600, color: event.price > 0 ? '#6366f1' : '#10b981' }}>
                                                    {event.price > 0 ? `$${event.price}` : 'FREE'}
                                                </span>
                                            </div>
                                            {event.capacity && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>
                                                    <Users size={14} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                                                    <span>Capacity: {event.capacity}</span>
                                                </div>
                                            )}
                                            {event.external_booking_url && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem' }}>
                                                    <LinkIcon size={14} style={{ color: '#10b981', flexShrink: 0 }} />
                                                    <a href={event.external_booking_url} target="_blank" rel="noreferrer" style={{ color: '#10b981', fontWeight: 600, textDecoration: 'none' }}>Booking URL attached ↗</a>
                                                </div>
                                            )}
                                        </div>

                                        {/* Tags */}
                                        {event.tags && event.tags.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                                                {event.tags.map((t, i) => (
                                                    <span key={i} style={{ fontSize: '0.75rem', background: 'rgba(99,102,241,0.08)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 20, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <Tag size={10} />{t}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* Expand toggle */}
                                        {event.description && event.description.length > 120 && (
                                            <button
                                                onClick={() => toggleExpand(event.id)}
                                                style={{ fontSize: '0.8rem', color: 'var(--admin-primary)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, padding: 0 }}
                                            >
                                                {isExpanded ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show more</>}
                                            </button>
                                        )}

                                        {/* Divider */}
                                        <div style={{ borderTop: '1px solid var(--admin-border)', marginTop: 'auto', paddingTop: 16 }}>
                                            {/* Action panel */}
                                            {showRejectDialog === event.id ? (
                                                <div style={{ background: '#fef2f2', borderRadius: 10, padding: 16, border: '1px solid #fecaca' }}>
                                                    <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#dc2626', marginBottom: 8, display: 'block' }}>
                                                        <AlertCircle size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                                        Rejection Reason (Optional — partner will see this)
                                                    </label>
                                                    <textarea
                                                        className="form-input" rows={3}
                                                        value={rejectionReason[event.id] || ''}
                                                        onChange={e => setRejectionReason({ ...rejectionReason, [event.id]: e.target.value })}
                                                        placeholder="Explain why this event was rejected..."
                                                        style={{ marginBottom: 10, fontSize: '0.88rem' }}
                                                    />
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button
                                                            onClick={() => handleReject(event.id)}
                                                            disabled={isLoading}
                                                            style={{ flex: 1, padding: '10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
                                                        >
                                                            {isLoading ? 'Rejecting...' : '✗ Confirm Rejection'}
                                                        </button>
                                                        <button
                                                            onClick={() => { setShowRejectDialog(null); setRejectionReason({ ...rejectionReason, [event.id]: '' }); }}
                                                            style={{ flex: 1, padding: '10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : showEditDialog === event.id ? (
                                                <div style={{ background: '#fffbeb', borderRadius: 10, padding: 16, border: '1px solid #fde68a' }}>
                                                    <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#d97706', marginBottom: 8, display: 'block' }}>
                                                        <MessageSquare size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                                        Edit Request Note — partner will receive this
                                                    </label>
                                                    <textarea
                                                        className="form-input" rows={3}
                                                        value={editNote[event.id] || ''}
                                                        onChange={e => setEditNote({ ...editNote, [event.id]: e.target.value })}
                                                        placeholder="Describe exactly what needs to be changed..."
                                                        style={{ marginBottom: 10, fontSize: '0.88rem' }}
                                                    />
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button
                                                            onClick={() => handleRequestEdits(event.id)}
                                                            disabled={isLoading}
                                                            style={{ flex: 1, padding: '10px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
                                                        >
                                                            {isLoading ? 'Sending...' : '✏️ Send Edit Request'}
                                                        </button>
                                                        <button
                                                            onClick={() => { setShowEditDialog(null); setEditNote({ ...editNote, [event.id]: '' }); }}
                                                            style={{ flex: 1, padding: '10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                    {/* Approve */}
                                                    <button
                                                        onClick={() => handleApprove(event.id)}
                                                        disabled={isLoading}
                                                        style={{
                                                            flex: 2, minWidth: 140, padding: '10px 18px',
                                                            background: 'linear-gradient(135deg, #10b981, #059669)',
                                                            color: '#fff', border: 'none', borderRadius: 10,
                                                            fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                                            boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                                                            transition: 'all 0.2s', opacity: isLoading ? 0.7 : 1
                                                        }}
                                                        onMouseOver={e => !isLoading && (e.currentTarget.style.transform = 'translateY(-1px)')}
                                                        onMouseOut={e => (e.currentTarget.style.transform = 'none')}
                                                    >
                                                        <Check size={16} />
                                                        {isLoading ? 'Approving...' : 'Approve & Publish'}
                                                    </button>

                                                    {/* Request Edits */}
                                                    <button
                                                        onClick={() => setShowEditDialog(event.id)}
                                                        disabled={isLoading}
                                                        style={{
                                                            flex: 1, minWidth: 120, padding: '10px 14px',
                                                            background: '#fffbeb', color: '#d97706',
                                                            border: '1.5px solid #fde68a', borderRadius: 10,
                                                            fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseOver={e => !isLoading && (e.currentTarget.style.background = '#fef3c7')}
                                                        onMouseOut={e => (e.currentTarget.style.background = '#fffbeb')}
                                                    >
                                                        <MessageSquare size={15} />
                                                        Request Edits
                                                    </button>

                                                    {/* Reject */}
                                                    <button
                                                        onClick={() => setShowRejectDialog(event.id)}
                                                        disabled={isLoading}
                                                        style={{
                                                            flex: 1, minWidth: 100, padding: '10px 14px',
                                                            background: '#fef2f2', color: '#dc2626',
                                                            border: '1.5px solid #fecaca', borderRadius: 10,
                                                            fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseOver={e => !isLoading && (e.currentTarget.style.background = '#fee2e2')}
                                                        onMouseOut={e => (e.currentTarget.style.background = '#fef2f2')}
                                                    >
                                                        <X size={15} />
                                                        Reject
                                                    </button>

                                                    {/* Preview (external link) */}
                                                    {event.external_booking_url && (
                                                        <a
                                                            href={event.external_booking_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                padding: '10px 14px',
                                                                background: '#f0f9ff', color: '#0ea5e9',
                                                                border: '1.5px solid #bae6fd', borderRadius: 10,
                                                                fontWeight: 600, fontSize: '0.88rem',
                                                                display: 'flex', alignItems: 'center', gap: 7,
                                                                textDecoration: 'none', whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            <Eye size={15} />
                                                            Preview Booking
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
